import React from 'react';
import classNames from 'classnames';
import request from 'utils/request_ajax';
import { routes } from 'constants/config';
import QuestionPrompt from 'components/Practice/Learner/QuestionPrompt';
import trackEvent from 'misc/trackEvent';
import Response from './Response/Response';

class VideoResponseQuestion extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      disabled: this.props.meta.finished || this.props.meta.previewing,
      givenAnswer: this.props.meta.previewing
        ? null
        : this.props.data.givenAnswer,
      givenAnswerIsIlos: this.props.data.givenAnswerIsIlos,
      skipped: this.props.meta.previewing ? null : this.props.meta.skipped,
      isPolling: false,
      recording: false,
      errorMessage: null,
      token: null,
      showUploadButton: this.props.meta.showUploadButton,
      videoUploading: false,
      technicalDifficultiesReported: false,
      sendFeedbackError: false,
      finished: this.props.meta.finished,
    };
    this.pollStatus = this.pollStatus.bind(this);
    this.loadIlosRecording = this.loadIlosRecording.bind(this);
    this.unloadWebcam = this.unloadWebcam.bind(this);
    this.skipRecording = this.skipRecording.bind(this);
    this.loadRecording = this.loadRecording.bind(this);
    this.saveVideo = this.saveVideo.bind(this);
    this.requestToken = this.requestToken.bind(this);
    this.isScreenRecording =
      this.props.data.questionType === 'screen_recording';
    this.isWebcamRecording =
      this.props.data.questionType === 'webcam_recording';
    this.recordWebcamEvent = this.recordWebcamEvent.bind(this);
  }

  componentDidMount() {
    const { data, meta } = this.props;
    const useIlosRecorder =
      this.isScreenRecording || (this.isWebcamRecording && meta.features.ilos);
    const showIlosUploadButton = useIlosRecorder && this.state.showUploadButton;

    if (this.isWebcamRecording && !meta.features.ilos && !data.givenAnswer) {
      this.loadRecording();
    }

    if ((this.isScreenRecording || showIlosUploadButton) && !data.givenAnswer) {
      this.loadIlosRecording();
    }
  }

  pollStatus() {
    if (this.state.givenAnswer || this.state.isPolling === false) return;

    if (this.props.meta.statusUrl) {
      request
        .get(this.props.meta.statusUrl)
        .then(payload => {
          if (
            this.state.recording ||
            this.isScreenRecording ||
            this.state.showUploadButton
          ) {
            if (payload.success && !payload.given_answer) {
              setTimeout(() => this.pollStatus(), 1000);
            } else if (payload.success) {
              this.setState({
                givenAnswer: payload.given_answer,
                givenAnswerIsIlos: payload.answer_is_ilos,
                isPolling: false,
                recording: false,
                token: null,
              });
            } else {
              this.setState({
                token: null,
                errorMessage: payload.error,
                isPolling: false,
                recording: false,
              });
            }
          }
        })
        .fail(payload => {
          this.setState({ errorMessage: 'Network error' });
        });
    }
  }

  loadIlosRecording() {
    const recording = this.isWebcamRecording && !this.state.showUploadButton;
    const useToken =
      recording || this.isScreenRecording || this.state.showUploadButton;

    this.setState(state => ({
      skipped: false,
      recording: this.isWebcamRecording && !state.showUploadButton,
      isPolling: true,
      givenAnswer: null,
    }));

    if (this.props.meta.tokenUrl) {
      request
        .get(this.props.meta.tokenUrl)
        .then(payload => {
          if (useToken) {
            if (payload.success) {
              this.setState(
                {
                  token: payload.token,
                },
                this.pollStatus,
              );
            } else {
              this.setState({
                errorMessage: payload.errorMessage,
              });
            }
          }
        })
        .fail(payload => {
          this.setState({ errorMessage: 'Network error' });
        });
    }
  }

  requestToken() {
    if (this.props.meta.tokenUrl) {
      request
        .get(this.props.meta.tokenUrl)
        .then(payload => {
          if (payload.success) {
            this.setState({ token: payload.token, givenAnswer: null });
          } else {
            this.setState({ errorMessage: payload.errorMessage });
          }
        })
        .fail(payload => {
          if (payload.responseJSON.error === 'lesson_completed') {
            this.setState({ finished: true });
          } else {
            this.setState({ errorMessage: 'Network error' });
          }
        });
    }
  }

  loadRecording() {
    this.setState(
      {
        skipped: false,
        recording: true,
        errorMessage: null,
        videoUploading: false,
        sendFeedbackError: false,
        technicalDifficultiesReported: false,
      },
      this.requestToken(),
    );
  }

  saveVideo() {
    if (this.props.meta.readyUrl) {
      request
        .postJSON(this.props.meta.readyUrl, { token: this.state.token })
        .then(() => {
          this.setState(state => ({
            givenAnswer: state.token,
          }));
        });
    }
  }

  unloadWebcam() {
    this.setState({
      token: null,
      givenAnswer: null,
      errorMessage: null,
      recording: false,
      isPolling: false,
      skipped: false,
    });
  }

  skipRecording(feedback, reason) {
    const { meta, data } = this.props;

    if (this.canSkipWithReasonAndFeedback(feedback, reason)) {
      request
        .post(meta.skippedUrl, {
          question_id: data.questionId,
          skipped: true,
          feedback,
          reason,
        })
        .then(responseData => {
          const answer = responseData.answer?.answer;
          this.trackMediaAnswerSkipped(reason, feedback, answer);
        });
      this.setWebcamRecordingStates();
      this.sendTechnicalIssueMailer(reason, feedback, true);
    } else if (this.canReportTechnicalDifficulties(feedback, reason)) {
      this.trackMediaAnswerSkipped(reason, feedback);
      this.sendTechnicalIssueMailer(reason, feedback, false);
    } else if (meta.skippedUrl) {
      request.post(meta.skippedUrl, {
        question_id: data.questionId,
        skipped: true,
      });
      this.setWebcamRecordingStates();
      this.setState({ skipped: true, errorMessage: null });
    }
  }

  canSkipWithReasonAndFeedback = (feedback, reason) => {
    return (
      feedback &&
      reason &&
      this.props.meta.skippedUrl &&
      !this.props.meta.features.removeAbilityToSkipRecordingQuestions
    );
  };

  canReportTechnicalDifficulties = (feedback, reason) => {
    return (
      feedback &&
      reason &&
      this.props.meta.skippedUrl &&
      this.props.meta.features.removeAbilityToSkipRecordingQuestions
    );
  };

  trackMediaAnswerSkipped = (reason, feedback, answer = null) => {
    trackEvent('practice/media_answer_skipped', {
      answer_id: answer?.id,
      question_id: this.props.data.questionId,
      question_type: this.props.data.questionType,
      skip_reason: reason,
      skip_feedback: feedback,
    });
  };

  setWebcamRecordingStates = () => {
    if (this.isWebcamRecording && this.props.meta.features.ilos) {
      this.setState({ recording: false, token: null });
    }
  };

  sendTechnicalIssueMailer = (reason, feedback, skippedState) => {
    request
      .post(
        routes.PRACTICE.TECHNICAL_ISSUES_MAILER.CREATE(
          this.props.data.questionId,
        ),
        {
          assignor_id: this.props.data.assignorId,
          skip_reason: reason,
          skip_feedback: feedback,
        },
      )
      .then(data => {
        if (!data.success) {
          this.setState({ sendFeedbackError: true });
        }
        this.setState({
          skipped: skippedState,
          technicalDifficultiesReported: !skippedState,
          errorMessage: null,
        });
      });
  };

  recordWebcamEvent(eventType) {
    const { progressId, questionId } = this.props.data;
    const eventTrackingData = {
      browser_information: true,
      event_type: eventType,
      event_data: {
        question_id: questionId,
        progress_id: progressId,
        token: this.state.token,
      },
    };
    request.post(routes.EVENTS, eventTrackingData);
  }

  handleBadUploadType = () => {
    this.setState({
      errorMessage: 'Invalid file type',
    });
  };

  render() {
    const { i18n, meta, hidePrompt } = this.props;
    const { criteria, questionId, progressId, isGradable } = this.props.data;
    const { finished, ilosLogo, features, apiKey, previewing } =
      this.props.meta;
    const {
      givenAnswer,
      skipped,
      recording,
      errorMessage,
      token,
      disabled,
      showUploadButton,
      givenAnswerIsIlos,
    } = this.state;
    const unskippedAnswer = !skipped && givenAnswer;
    const useIlosLogo =
      this.isScreenRecording || (features.ilos && this.isWebcamRecording);
    const containerClasses = classNames(
      'PracticeQuestion VideoResponseQuestion',
      {
        // this const can be removed and the two classnames that are always present can be added back to the JSX below when ilos is no longer being used for screen recording and this component does not have a footer
        'QuestionElement--practice': this.isScreenRecording,
      },
    );
    return (
      <div>
        <div className={containerClasses}>
          {features.practice &&
            (criteria.length > 0 || meta.exampleRecordingHtml) &&
            !hidePrompt && (
              <QuestionPrompt
                i18n={i18n}
                criteria={criteria}
                features={features}
                exampleRecordingHtml={meta.exampleRecordingHtml}
              />
            )}
          <Response
            questionId={questionId}
            i18n={i18n}
            givenAnswer={givenAnswer}
            givenAnswerIsIlos={givenAnswerIsIlos}
            skipped={skipped}
            recording={recording}
            errorMessage={errorMessage}
            token={token}
            disabled={disabled}
            finished={this.state.finished}
            showAnswerAlreadySubmitted={!finished && this.state.finished}
            showUploadButton={showUploadButton}
            loadIlosRecording={this.loadIlosRecording}
            skipRecording={this.skipRecording}
            unloadWebcam={this.unloadWebcam}
            isWebcamRecording={this.isWebcamRecording}
            isScreenRecording={this.isScreenRecording}
            unskippedAnswer={unskippedAnswer}
            hasIlosFeature={features.ilos}
            canGatherDataOnTechnicalIssues={
              features.gatherDataOnTechnicalIssues
            }
            loadRecording={this.loadRecording}
            saveVideo={this.saveVideo}
            videoUploading={this.state.videoUploading}
            setUploadFinished={() => this.setState({ videoUploading: false })}
            setUploadStarted={() => this.setState({ videoUploading: true })}
            apiKey={apiKey}
            previewing={previewing}
            webcamPlaybackEnabled={features.webcamPlayback}
            recordWebcamEvent={this.recordWebcamEvent}
            progressId={progressId}
            removeAbilityToSkip={features.removeAbilityToSkipRecordingQuestions}
            hasAnswerBeenSubmitted={!!this.state.givenAnswer}
            isGradable={isGradable}
            handleBadUploadType={this.handleBadUploadType}
            recordingSupported={meta.recordingSupported}
            technicalDifficultiesReported={
              this.state.technicalDifficultiesReported
            }
            sendFeedbackError={this.state.sendFeedbackError}
          />
        </div>
        {!finished && useIlosLogo && (
          <div className="mt-4 VideoResponseQuestion-footer">
            <div className="d-f ai-c jc-e fw-w">
              {this.isWebcamRecording
                ? i18n.video_response.webcam_footer
                : i18n.video_response.screen_recording_footer}
              <img
                alt="video recording provider logo"
                className="VideoResponseQuestion-footer--ilosLogo"
                src={ilosLogo}
              />
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default VideoResponseQuestion;
